import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Authenticator, useAuthenticator } from '@aws-amplify/ui-react';
import { fetchAuthSession } from 'aws-amplify/auth';
import { useTranslation } from 'react-i18next';
import { invitationsApi } from '@/api/invitationsWrapper';
import type { ValidateInvitationTokenResponse } from 'we-tree-api-client/v2';
import { useDomainStore } from '@/hooks/useStores';
import AuthGatePanel from '@/components/auth/AuthGatePanel';
import { authComponents, authFormFields, authSocialProviders, SOCIAL_LOGIN_ENABLED } from '@/config/authUi';
import { PENDING_INVITATION_TOKEN_KEY } from '@/config/invitationStorage';
import '@/styles/amplify-auth.css';
import './accept-invitation-screen.css';

/**
 * AcceptInvitationScreen - Public screen for accepting invitations
 * Validates invitation token and conditionally enables sign-up in Amplify Authenticator
 */
const AcceptInvitationScreen: React.FC = () => {
  const { t } = useTranslation('screens');
  const { t: tForms } = useTranslation('forms');
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [token] = useState<string | null>(searchParams.get('token'));
  const [validationResult, setValidationResult] = useState<ValidateInvitationTokenResponse | null>(null);
  const [isValidating, setIsValidating] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Validate token on mount
  useEffect(() => {
    const validateToken = async () => {
      if (!token) {
        setError(t('acceptInvitation.noToken'));
        setIsValidating(false);
        return;
      }

      try {
        const result = await invitationsApi.validateInvitationToken(token);
        if (result.success) {
          setValidationResult(result.data);
          // Survive Cognito Hosted UI / social OAuth redirect back to `/`
          sessionStorage.setItem(PENDING_INVITATION_TOKEN_KEY, token);
        } else {
          setError(result.error?.message || t('acceptInvitation.invalidToken'));
        }
      } catch {
        setError(t('acceptInvitation.validateFailed'));
      } finally {
        setIsValidating(false);
      }
    };

    validateToken();
  }, [token, t]);

  // Show loading state while validating
  if (isValidating) {
    return (
      <div className="accept-invitation-loading">
        <div>{t('acceptInvitation.validating')}</div>
      </div>
    );
  }

  // Show error state
  if (error || !validationResult?.valid) {
    return (
      <div className="accept-invitation-error">
        <h1>{t('acceptInvitation.invalidTitle')}</h1>
        <p>{error || validationResult?.error || t('acceptInvitation.noLongerValid')}</p>
        <p className="accept-invitation-error-message">{t('acceptInvitation.expiredHint')}</p>
        <button onClick={() => navigate('/')} className="accept-invitation-error-button">
          {tForms('actions.goToHome')}
        </button>
      </div>
    );
  }

  const inviteEmail = validationResult.email;

  return (
    <Authenticator.Provider>
      <div className="auth-shell auth-shell--gate accept-invitation-screen">
        <AuthGatePanel showIllus={false} />
        <div className="auth-shell__form-column">
          <div className="auth-form-card accept-invitation-form-card">
            <div className="accept-invitation-banner">
              <h2>
                {t('acceptInvitation.invitedTo')}{' '}
                <span className="accept-invitation-banner-family-name">{validationResult.familyName}</span>
              </h2>
              <p className="accept-invitation-banner-text">
                {SOCIAL_LOGIN_ENABLED
                  ? inviteEmail
                    ? t('acceptInvitation.bannerSocialWithEmail', { email: inviteEmail })
                    : t('acceptInvitation.bannerSocialNoEmail')
                  : t('acceptInvitation.bannerEmailOnly')}
              </p>
            </div>

            <Authenticator
              hideSignUp={validationResult.userExists === true}
              initialState={validationResult.userExists === true ? 'signIn' : 'signUp'}
              socialProviders={authSocialProviders}
              formFields={authFormFields}
              components={authComponents}
            >
              <InvitationAcceptedHandler token={token} validationResult={validationResult} navigate={navigate} />
            </Authenticator>
          </div>
        </div>
      </div>
    </Authenticator.Provider>
  );
};

/**
 * Component that handles invitation acceptance after authentication
 */
const InvitationAcceptedHandler: React.FC<{
  token: string | null;
  validationResult: ValidateInvitationTokenResponse | null;
  navigate: (path: string) => void;
}> = ({ token, validationResult, navigate }) => {
  const { t } = useTranslation('screens');
  const { authStatus } = useAuthenticator();
  const domainStore = useDomainStore();
  const [isAccepting, setIsAccepting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const acceptInvitation = async () => {
      if (authStatus !== 'authenticated' || !token || !validationResult?.valid) {
        return;
      }

      const acceptedKey = `invitation_accepted_${token}`;
      if (sessionStorage.getItem(acceptedKey)) {
        return;
      }

      // Wait for the user to be registered in the database
      // UserContext registers the user asynchronously, so we need to wait for that
      let userRetries = 0;
      const maxUserRetries = 30; // 30 * 200ms = 6 seconds max wait
      const userRetryDelay = 200; // 200ms between retries

      const waitForUser = async (): Promise<boolean> => {
        // Check if user is initialized in DomainStore
        if (domainStore.user) {
          return true;
        }
        // If there's an error, don't wait
        if (domainStore.userInitializationError) {
          return false;
        }
        return false;
      };

      // Wait for user registration to complete
      while (userRetries < maxUserRetries) {
        const userReady = await waitForUser();
        if (userReady) {
          break;
        }
        userRetries++;
        if (userRetries < maxUserRetries) {
          await new Promise((resolve) => setTimeout(resolve, userRetryDelay));
        }
      }

      if (userRetries >= maxUserRetries) {
        if (domainStore.userInitializationError) {
          setError(t('acceptInvitation.userRegFailed', { error: domainStore.userInitializationError }));
        } else {
          setError(t('acceptInvitation.userRegSlow'));
        }
        return;
      }

      // Wait for the access token to be available before making the API call
      // This handles the race condition where authStatus is 'authenticated'
      // but the token hasn't been fully initialized yet
      let retries = 0;
      const maxRetries = 10;
      const retryDelay = 200; // 200ms between retries

      const waitForToken = async (): Promise<boolean> => {
        try {
          const session = await fetchAuthSession();
          const accessToken = session.tokens?.accessToken?.toString();
          if (accessToken) {
            return true;
          }
        } catch {
          // Token not available yet, continue waiting
        }
        return false;
      };

      // Wait for token with retries
      while (retries < maxRetries) {
        const hasToken = await waitForToken();
        if (hasToken) {
          break;
        }
        retries++;
        if (retries < maxRetries) {
          await new Promise((resolve) => setTimeout(resolve, retryDelay));
        }
      }

      if (retries >= maxRetries) {
        setError(t('acceptInvitation.authTokenUnavailable'));
        return;
      }

      setIsAccepting(true);
      try {
        const result = await invitationsApi.acceptInvitation(token);
        if (result.success) {
          sessionStorage.setItem(acceptedKey, 'true');
          sessionStorage.removeItem(PENDING_INVITATION_TOKEN_KEY);
          navigate(`/families/${result.data.familyId}/overview`);
        } else {
          setError(result.error?.message || t('acceptInvitation.acceptFailed'));
        }
      } catch {
        setError(t('acceptInvitation.acceptFailed'));
      } finally {
        setIsAccepting(false);
      }
    };

    acceptInvitation();
  }, [authStatus, token, validationResult, navigate, domainStore, t]);

  if (isAccepting) {
    return (
      <div className="accept-invitation-handler">
        <div className="accept-invitation-handler-success">{t('acceptInvitation.accepting')}</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="accept-invitation-handler">
        <div className="accept-invitation-handler-error">{error}</div>
      </div>
    );
  }

  return (
    <div className="accept-invitation-handler">
      <div className="accept-invitation-handler-text">{t('acceptInvitation.welcome')}</div>
    </div>
  );
};

export default AcceptInvitationScreen;
